import { useState, useEffect, useRef } from 'react';
import { Check, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Neighborhood mapping
const NEIGHBORHOOD_MAP = {
  '/pasadena': 'Pasadena',
  '/la-canada-flintridge': 'La Cañada Flintridge',
  '/san-marino': 'San Marino',
  '/arcadia': 'Arcadia',
  '/mobile': 'Los Angeles County',
  '/lawn-sign': 'Los Angeles County',
  '/neighbor': 'Los Angeles County'
};

export default function LandingPageForm({ 
  cityName, 
  neighborhood,
  sourceTag,
  headline,
  description,
  pageTitle
}) {
  const [formData, setFormData] = useState({
    full_name: '',
    property_address: '',
    phone: '',
    email: ''
  });
  
  const [attribution, setAttribution] = useState({
    landing_page: '',
    page_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    referrer: '',
    neighborhood: neighborhood,
    lead_type: 'property_assessment'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState('');
  const addressInputRef = useRef(null);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' }
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, property_address: place.formatted_address }));
        }
      });
    }
  }, [isSubmitted]); // Re-initialize if form resets

  // Capture UTM parameters and attribution data on page load
  useEffect(() => {
    // Set page title
    document.title = pageTitle || `${cityName} Property Care | Sunlight Grounds Care`;
    
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    
    setAttribution({
      landing_page: pathname,
      page_url: window.location.href,
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_term: urlParams.get('utm_term') || '',
      utm_content: urlParams.get('utm_content') || '',
      referrer: document.referrer || '',
      neighborhood: NEIGHBORHOOD_MAP[pathname] || neighborhood,
      lead_type: 'property_assessment'
    });
  }, [neighborhood]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(`${position.coords.latitude}, ${position.coords.longitude}`);
          },
          () => {
            resolve('Location not available');
          },
          { timeout: 5000 }
        );
      } else {
        resolve('Geolocation not supported');
      }
    });
  };

  const fireGAEvent = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        utm_campaign: attribution.utm_campaign,
        landing_page: attribution.landing_page,
        neighborhood: attribution.neighborhood,
        lead_type: 'property_assessment'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate phone format (at least 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number.');
      setIsSubmitting(false);
      return;
    }

    // Validate SMS consent
    if (!smsConsent) {
      setError('You must agree to receive SMS messages to submit this form.');
      setIsSubmitting(false);
      return;
    }

    const locationData = await getLocation();
    const submittedAt = new Date().toISOString();

    try {
      const payload = {
        // Visible form fields
        name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        property_type: 'residential',
        message: `Property Address: ${formData.property_address}`,
        intent: 'assessment',
        source: sourceTag,
        location: locationData,
        // Attribution fields
        landing_page: attribution.landing_page,
        page_url: attribution.page_url,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_term: attribution.utm_term,
        utm_content: attribution.utm_content,
        referrer: attribution.referrer,
        neighborhood: attribution.neighborhood,
        lead_type: attribution.lead_type,
        sms_consent: smsConsent,
        consent_timestamp: smsConsent ? new Date().toISOString() : ''
      };

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Fire GA4 generate_lead event
        fireGAEvent();
        setIsSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Structured Landscape Maintenance',
    'Professional Pressure Washing',
    'Exterior Property Detailing & Cleaning'
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
              Thank You!
            </h2>
            <p className="text-slate-600">
              We've received your request and will contact you as soon as possible to schedule your complimentary property assessment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`${neighborhood.toLowerCase().replace(/\s+/g, '-')}-landing-page`}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 flex flex-col items-center">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/m2jsgssv_grounds%20care%20copy%20.png"
              alt="Sunlight Grounds Care"
              className="h-12 w-auto hover:opacity-80 transition-opacity cursor-pointer"
            />
          </a>
          <p className="text-slate-600 text-sm mt-2">
            Professional Exterior Property Maintenance
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Content */}
            <div>
              {/* Hero Image */}
              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/r7pg4pr4_iStock-2155879454.jpg"
                  alt="Professional landscaped property"
                  className="w-full h-64 object-cover"
                />
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-[#1a2e44] mb-4 leading-tight">
                {headline || `Professional Exterior Property Care for ${cityName} Homes`}
              </h1>
              
              {/* Trust Line */}
              <p className="text-lg text-slate-600 mb-8">
                {description || `Structured exterior maintenance programs for ${cityName}'s finest properties.`}
              </p>
              
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#1a2e44] flex-shrink-0" />
                    <span className="text-lg text-slate-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column - Form */}
            <div className="lg:sticky lg:top-8">
              <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-slate-200">
                <h2 className="text-xl font-bold text-[#1a2e44] mb-6 text-center">
                  Schedule a Complimentary Property Assessment
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* 1. Full Name (required) */}
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-slate-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-[#1a2e44] focus:border-transparent outline-none transition-all"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>

                  {/* 2. Phone Number (required) */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-[#1a2e44] focus:border-transparent outline-none transition-all"
                      placeholder="(555) 555-5555"
                      data-testid="input-phone"
                    />
                  </div>

                  {/* SMS Consent (required, directly below phone) */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="sms_consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      required
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1a2e44] focus:ring-[#1a2e44]"
                      data-testid="sms-consent-checkbox"
                    />
                    <label htmlFor="sms_consent" className="text-xs text-slate-600 leading-relaxed">
                      By submitting this form, you agree to receive text messages from Sunlight Grounds Care regarding appointments, services, and account updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.
                    </label>
                  </div>

                  {/* 3. Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-[#1a2e44] focus:border-transparent outline-none transition-all"
                      placeholder="you@example.com"
                      data-testid="input-email"
                    />
                  </div>

                  {/* 4. Property Address (service detail) */}
                  <div>
                    <label htmlFor="property_address" className="block text-sm font-medium text-slate-700 mb-1">
                      Property Address
                    </label>
                    <input
                      ref={addressInputRef}
                      type="text"
                      id="property_address"
                      name="property_address"
                      value={formData.property_address}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-[#1a2e44] focus:border-transparent outline-none transition-all"
                      placeholder="Start typing your address..."
                      data-testid="input-address"
                      autoComplete="off"
                    />
                  </div>

                  {/* Hidden Attribution Fields */}
                  <input type="hidden" name="landing_page" value={attribution.landing_page} />
                  <input type="hidden" name="page_url" value={attribution.page_url} />
                  <input type="hidden" name="utm_source" value={attribution.utm_source} />
                  <input type="hidden" name="utm_medium" value={attribution.utm_medium} />
                  <input type="hidden" name="utm_campaign" value={attribution.utm_campaign} />
                  <input type="hidden" name="utm_term" value={attribution.utm_term} />
                  <input type="hidden" name="utm_content" value={attribution.utm_content} />
                  <input type="hidden" name="referrer" value={attribution.referrer} />
                  <input type="hidden" name="neighborhood" value={attribution.neighborhood} />
                  <input type="hidden" name="lead_type" value={attribution.lead_type} />

                  {error && (
                    <p className="text-red-600 text-sm" data-testid="form-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-4 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request My Property Assessment'}
                  </button>
                </form>

                {/* Below submit - compliance text */}
                <p className="text-center text-slate-400 text-xs mt-3">
                  Msg & data rates may apply.
                </p>

                {/* Privacy Policy / Terms links near form */}
                <div className="flex items-center justify-center gap-3 mt-2">
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1a2e44] text-xs underline hover:text-[#0f1d2d]" data-testid="form-privacy-link">
                    Privacy Policy
                  </a>
                  <span className="text-slate-300 text-xs">|</span>
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1a2e44] text-xs underline hover:text-[#0f1d2d]" data-testid="form-terms-link">
                    Terms & Conditions
                  </a>
                </div>

                {/* Trust Bar */}
                <p className="text-center text-slate-500 text-sm mt-4">
                  Locally operated. Professional uniformed teams.
                </p>

                {/* Secondary Contact Option */}
                <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                  <p className="text-slate-600 text-sm mb-2">Prefer to speak with us?</p>
                  <a 
                    href="tel:6267372797" 
                    className="inline-flex items-center gap-2 text-[#1a2e44] font-bold text-lg hover:text-[#0f1d2d] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    (626) 737-2797
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#1a2e44] py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Sunlight Grounds Care
          </p>
          <p className="text-slate-500 text-xs mt-1">
            Serving {cityName} & Los Angeles County
          </p>
          <div className="flex items-center justify-center gap-3 mt-2">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-300 text-xs transition-colors" data-testid="landing-footer-privacy">
              Privacy Policy
            </a>
            <span className="text-slate-600 text-xs">|</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-300 text-xs transition-colors" data-testid="landing-footer-terms">
              SMS Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
